import { useCallback } from 'react';

export const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length == 2) return parts.pop().split(';').shift();
};

/**
 * Hook for setting cookies within React components.
 * Automatically uses the configuration from CookieProvider.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const setCookie = useSetCookie();
 *
 *   const handleLogin = () => {
 *     setCookie({
 *       key: 'authToken',
 *       value: 'abc123',
 *       expires: new Date(Date.now() + 24 * 60 * 60 * 1000) // 1 day
 *     });
 *   };
 * }
 * ```
 *
 * @returns {function} Function to set cookies
 */
export const useSetCookie = () => {
  return useCallback((data: SetCookieProps) => {
    setRealCookie({
      path: '/',
      ...data,
    });
  }, []);
};

// Legacy function for backwards compatibility - requires config parameter
export const setCookie = (data: SetCookieProps) => {
  setRealCookie({
    path: '/',
    ...data,
  });
};

const setRealCookie = ({ key, value: valueParam, ...cookieOptions }: SetCookieProps): void => {
  let value = valueParam;

  if (Array.isArray(value) || Object.prototype.toString.call(value) === '[object Object]') {
    value = JSON.stringify(value);
  }

  let cookie = `${key}=${value}`;
  if (cookieOptions.expires) {
    cookie += `; Expires=${cookieOptions.expires.toUTCString()}`;
  }
  if (cookieOptions.path) {
    cookie += `; Path=${cookieOptions.path}`;
  }
  if (cookieOptions.domain) {
    cookie += `; Domain=${cookieOptions.domain}`;
  }
  if (cookieOptions.maxAge) {
    cookie += `; Max-Age=${cookieOptions.maxAge}`;
  }
  if (cookieOptions.sameSite) {
    cookie += `; SameSite=${cookieOptions.sameSite}`;
  }
  if (cookieOptions.secure) {
    cookie += '; Secure';
  }
  if (cookieOptions.httpOnly) {
    cookie += '; HttpOnly';
  }

  document.cookie = cookie;
};

export type SetCookieProps = {
  domain?: string;
  expires?: Date;
  httpOnly?: boolean;
  maxAge?: number;
  key: string;
  path?: string;
  sameSite?: string;
  secure?: boolean;

  value: string;
};
